import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { store } from '../../../config/firebase';
import { liveGameDocs } from '../../../helpers/games';
import GameByStatusCards from '../gameStatusCards/gameByStatusCards';
import { useAuth } from '../../../contexts/authContext';
import { buildBracketLayout } from '../../../helpers/bracketLayout';
import { getMatchGame, getOrderedRounds, getRoundTitle } from '../../../helpers/bracketData';
import {
  findScheduleEntryForBracketRound,
  formatScheduleDateTime
} from '../../../helpers/roundSchedule';

import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import NodePlayers from './nodePlayers';
import MatchCard from './matchCard';
import MobileBracket from './mobileBracket';
import AddGameDialogAlt from '../../games/addGameDialogAlt';

const DEFAULT_TRANSFORM_STATE = {
  scale: 1,
  positionX: 0,
  positionY: 0
};

const SingleBracket = ({ bracket, event }) => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMatch, setSelectedMatch] = useState(null);
  const [selectedMatchNode, setSelectedMatchNode] = useState(null);
  const [selectedGameData, setSelectedGameData] = useState(null);
  const [openGame, setOpenGame] = useState(false);
  const [openBracketEdit, setOpenBracketEdit] = useState(false);
  const [openGameAddEdit, setOpenGameAddEdit] = useState(false);
  const [connectorLines, setConnectorLines] = useState([]);
  const [cardHeight, setCardHeight] = useState(0);
  // Phones default to one round per screen; the full tree (with its connectors)
  // stays available behind the Rounds/Bracket toggle.
  const [mobileView, setMobileView] = useState('rounds');
  const { authenticated } = useAuth();
  const containerRef = useRef(null);
  const contentInnerRef = useRef(null);
  const matchRefs = useRef({});
  const transformRef = useRef(null);
  const transformStateRef = useRef(DEFAULT_TRANSFORM_STATE);
  const mobileTreeFitRef = useRef(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const showTree = !isMobile || mobileView === 'bracket';
  const zoomLimits = useMemo(
    () => ({ min: isMobile ? 0.15 : 0.5, max: 2, step: 0.2 }),
    [isMobile]
  );
  const appBarHeight = isMobile ? 56 : 64;
  const viewportHeight = `calc(100dvh - ${appBarHeight + 48}px)`;

  useEffect(() => {
    transformStateRef.current = DEFAULT_TRANSFORM_STATE;
  }, [bracket?.id]);

  useEffect(() => {
    //Get all games from firebase connected to this bracket.
    setLoading(true);
    setGames([]);
    const collectionRef = collection(store, 'games');
    const q = query(collectionRef, where('bracket', '==', bracket.id));

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const gamesData = liveGameDocs(querySnapshot).map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data()
        }));

        setGames(gamesData);
        setLoading(false);
      },
      (error) => {
        console.error('Bracket games listener failed:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [bracket]);

  useEffect(() => {
    if (!openBracketEdit || !selectedMatchNode) return;

    const latestMatch = (bracket.matches || []).find((m) => m.id === selectedMatchNode.id);
    if (latestMatch) {
      setSelectedMatchNode(latestMatch);
    }
  }, [bracket, openBracketEdit, selectedMatchNode?.id, selectedMatchNode]);

  //Set selected game again when games change.
  useEffect(() => {
    if (selectedMatch) {
      const updatedGame = games.find((game) => game.id === selectedMatch.id);
      if (updatedGame) {
        setSelectedMatch(updatedGame);
      } else {
        setSelectedMatch(selectedMatch);
      }
    }
  }, [games, selectedMatch]);

  const handleNodePlayersUpdate = (updatedMatch) => {
    setSelectedMatchNode(updatedMatch);
  };

  const bracketRounds = useMemo(
    () => getOrderedRounds(bracket?.matches || []),
    [bracket?.matches]
  );

  // One-sided tree: every round runs left to right and each match sits at the
  // average of its two sides. buildBracketLayout falls back to a per-round
  // stack if the stored tree is malformed, so every match is always rendered.
  const layoutData = useMemo(
    () =>
      buildBracketLayout({
        matches: bracket?.matches || [],
        cardHeight,
        gap: 96
      }),
    [bracket?.matches, cardHeight]
  );

  const handleOpenGame = (game) => {
    setSelectedMatch(game);
    setOpenGame(true);
  };

  const handleOpenBracketNode = (matchNode) => {
    setSelectedMatchNode(matchNode);
    setOpenBracketEdit(true);
  };

  const handleGameEditOpen = (
    teamAId,
    teamBId,
    customName,
    bracketId,
    matchId,
    matchLocation
  ) => {
    setSelectedGameData({ teamAId, teamBId, customName, bracketId, matchId, matchLocation });
    setOpenGameAddEdit(true);
  };

  const handleGameEditClose = () => {
    setSelectedGameData(null);
    setOpenGameAddEdit(false);
  };

  const renderMatchCard = (match, refCallback, roundTitle) => (
    <MatchCard
      key={match.id}
      ref={refCallback}
      match={match}
      game={getMatchGame(games, match.id)}
      bracketId={bracket.id}
      authenticated={authenticated}
      isMobile={isMobile}
      loading={loading}
      scheduleText={formatScheduleDateTime(
        findScheduleEntryForBracketRound(event?.roundSchedule, roundTitle)
      )}
      onEdit={handleOpenBracketNode}
      onAddGame={handleGameEditOpen}
      onViewGame={handleOpenGame}
    />
  );

  // The tallest card sets the pitch. Cards are not all the same height: the
  // Add Game / View Game footer only exists on a card whose two sides are real
  // teams, so a pre-event bracket's first card is shorter than a played one.
  useEffect(() => {
    if (!showTree || !bracket?.matches?.length) {
      return undefined;
    }

    let frame;
    const measureHeight = () => {
      frame = requestAnimationFrame(() => {
        const nodes = Object.values(matchRefs.current).filter(Boolean);
        if (!nodes.length) {
          return;
        }

        const heights = nodes.map((node) => node.getBoundingClientRect().height);
        const tallest = Math.max(...heights);
        if (tallest > 0) {
          setCardHeight((current) => (Math.abs(current - tallest) > 0.5 ? tallest : current));
        }
      });
    };

    measureHeight();
    window.addEventListener('resize', measureHeight);

    let cancelled = false;
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        if (!cancelled) {
          measureHeight();
        }
      });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('resize', measureHeight);
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [bracket?.matches, games, authenticated, isMobile, showTree, event?.roundSchedule]);

  useEffect(() => {
    if (!showTree) {
      setConnectorLines([]);
      return undefined;
    }

    const content = contentInnerRef.current;
    if (!content) {
      return undefined;
    }

    const getRelativeOffset = (node, ancestor) => {
      let current = node;
      let offsetX = 0;
      let offsetY = 0;

      while (current && current !== ancestor) {
        offsetX += current.offsetLeft;
        offsetY += current.offsetTop;
        current = current.offsetParent;
      }

      if (current === ancestor) {
        return { x: offsetX, y: offsetY };
      }

      const nodeRect = node.getBoundingClientRect();
      const ancestorRect = ancestor.getBoundingClientRect();
      return { x: nodeRect.left - ancestorRect.left, y: nodeRect.top - ancestorRect.top };
    };

    let frame;
    const measure = () => {
      frame = requestAnimationFrame(() => {
        const nextLines = [];
        (bracket?.matches || []).forEach((match) => {
          if (!match.nextMatchId) {
            return;
          }

          const fromEl = matchRefs.current[match.id];
          const toEl = matchRefs.current[match.nextMatchId];
          if (!fromEl || !toEl) {
            return;
          }

          const fromPos = getRelativeOffset(fromEl, content);
          const toPos = getRelativeOffset(toEl, content);

          const y1 = fromPos.y + fromEl.offsetHeight / 2;
          const y2 = toPos.y + toEl.offsetHeight / 2;

          // One-sided layout: the destination is always the next column to
          // the right.
          const x1 = fromPos.x + fromEl.offsetWidth;
          const x2 = toPos.x;

          const midX = x1 + (x2 - x1) / 2;

          nextLines.push({ x1, y1, x2, y2, midX });
        });

        setConnectorLines(nextLines);
      });
    };

    measure();
    window.addEventListener('resize', measure);

    return () => {
      window.removeEventListener('resize', measure);
      if (frame) {
        cancelAnimationFrame(frame);
      }
    };
  }, [bracket?.matches, bracketRounds, isMobile, showTree, cardHeight]);

  // On a phone the tree is wider than the screen, so the first view of it is
  // scaled down to fit both dimensions. Doing it once (rather than on every
  // measurement) leaves the user's own pinch/pan untouched.
  useEffect(() => {
    if (!isMobile || mobileView !== 'bracket') {
      mobileTreeFitRef.current = false;
      return undefined;
    }

    if (mobileTreeFitRef.current || !bracket?.matches?.length || !cardHeight) {
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      const content = contentInnerRef.current;
      const container = containerRef.current;
      if (!content || !container) {
        return;
      }

      const contentWidth = content.offsetWidth;
      const contentHeight = content.offsetHeight;
      const availableWidth = container.clientWidth - 16;
      const availableHeight = container.clientHeight - 16;
      if (!contentWidth || !contentHeight || availableWidth <= 0 || availableHeight <= 0) {
        return;
      }

      const scale = Math.min(
        1,
        Math.max(
          zoomLimits.min,
          Math.min(availableWidth / contentWidth, availableHeight / contentHeight)
        )
      );
      const positionX = Math.max(8, (container.clientWidth - contentWidth * scale) / 2);
      const positionY = 8;

      mobileTreeFitRef.current = true;
      transformStateRef.current = { scale, positionX, positionY };
      transformRef.current?.setTransform(positionX, positionY, scale, 0);
    });

    return () => cancelAnimationFrame(frame);
  }, [isMobile, mobileView, bracket?.id, bracket?.matches, cardHeight, zoomLimits.min]);

  const renderTree = () => (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', minWidth: 0 }}>
      <TransformWrapper
        minScale={zoomLimits.min}
        maxScale={zoomLimits.max}
        initialScale={DEFAULT_TRANSFORM_STATE.scale}
        initialPositionX={DEFAULT_TRANSFORM_STATE.positionX}
        initialPositionY={DEFAULT_TRANSFORM_STATE.positionY}
        limitToBounds={false}
        centerOnInit={false}
        centerZoomedOut={false}
        alignmentAnimation={{ disabled: true }}
        wheel={{ step: zoomLimits.step }}
        doubleClick={{ disabled: true }}
        panning={{ velocityDisabled: true }}
        onInit={(ref) => {
          transformRef.current = ref;
          const { scale, positionX, positionY } = transformStateRef.current;

          if (scale !== 1 || positionX !== 0 || positionY !== 0) {
            ref.setTransform(positionX, positionY, scale, 0);
          }
        }}
        onTransformed={(ref, state) => {
          transformRef.current = ref;
          transformStateRef.current = state;
        }}>
        {() => (
          <Box
            ref={containerRef}
            onDragStart={(event) => event.preventDefault()}
            sx={{
              width: '100%',
              height: viewportHeight,
              maxWidth: '100%',
              minWidth: 0,
              overflow: 'hidden',
              position: 'relative',
              p: '10px',
              cursor: 'grab',
              userSelect: 'none',
              touchAction: 'none',
              overscrollBehaviorX: 'contain',
              overscrollBehaviorY: 'contain'
            }}>
            <TransformComponent
              wrapperStyle={{ width: '100%', height: '100%', overflow: 'hidden' }}
              contentStyle={undefined}>
              <Box
                ref={contentInnerRef}
                display="inline-flex"
                alignItems="flex-start"
                gap={6}
                sx={{ position: 'relative', width: 'max-content', height: 'max-content' }}>
                <Box
                  component="svg"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    pointerEvents: 'none'
                  }}>
                  {connectorLines.map((line, index) => (
                    <path
                      key={`${line.x1}-${line.y1}-${index}`}
                      d={`M ${line.x1} ${line.y1} L ${line.midX} ${line.y1} L ${line.midX} ${line.y2} L ${line.x2} ${line.y2}`}
                      fill="none"
                      stroke={
                        theme.palette.mode === 'dark'
                          ? theme.palette.text.primary
                          : theme.palette.primary.light
                      }
                      strokeWidth="2"
                    />
                  ))}
                </Box>

                <Box display="flex" alignItems="flex-start" gap={6}>
                  {bracketRounds.map(({ round, matches }) => {
                    // Top-to-bottom order follows the tree positions, so the
                    // DOM order (and round title) matches what is drawn.
                    const orderedMatches = matches
                      .slice()
                      .sort(
                        (a, b) =>
                          (layoutData.positions[a.id] || 0) -
                          (layoutData.positions[b.id] || 0)
                      );
                    const roundTitle = getRoundTitle(round, orderedMatches);

                    return (
                      <Box
                        key={round}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        sx={{ minWidth: 260, position: 'relative' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {roundTitle}
                        </Typography>
                        <Box
                          sx={{ position: 'relative', width: '100%', height: layoutData.height }}>
                          {orderedMatches.map((match) => (
                            <Box
                              key={match.id}
                              sx={{
                                position: 'absolute',
                                top: layoutData.positions[match.id] || 0,
                                left: 0,
                                right: 0,
                                display: 'flex',
                                justifyContent: 'center'
                              }}>
                              {renderMatchCard(match, (node) => {
                                if (node) {
                                  matchRefs.current[match.id] = node;
                                } else {
                                  delete matchRefs.current[match.id];
                                }
                              }, roundTitle)}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </TransformComponent>
          </Box>
        )}
      </TransformWrapper>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={0.75}
            sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Display bracket as:
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={mobileView}
              onChange={(event, value) => {
                if (value) {
                  setMobileView(value);
                }
              }}
              aria-label="Bracket view"
              sx={{
                '& .MuiToggleButton-root': {
                  py: 0.25,
                  px: 1,
                  fontSize: 11,
                  lineHeight: 1.4
                }
              }}>
              <ToggleButton value="rounds" aria-label="List view, one round per screen">
                List
              </ToggleButton>
              <ToggleButton value="bracket" aria-label="Classic bracket with connectors">
                Classic
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {mobileView === 'bracket' ? (
            renderTree()
          ) : (
            <MobileBracket rounds={bracketRounds} renderMatch={renderMatchCard} />
          )}
        </>
      ) : (
        renderTree()
      )}

      {selectedMatch && (
        <Dialog open={openGame} onClose={() => setOpenGame(false)} fullScreen={isMobile}>
          <DialogContent>
            <GameByStatusCards game={selectedMatch} />
            {/* <Typography variant="caption">{selectedMatch.id}</Typography> */}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => setOpenGame(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {selectedGameData && (
        <AddGameDialogAlt
          teamAId={selectedGameData.teamAId}
          teamBId={selectedGameData.teamBId}
          customName={selectedGameData.customName}
          bracketId={selectedGameData.bracketId}
          matchId={selectedGameData.matchId}
          matchLocation={selectedGameData.matchLocation}
          event={event}
          open={openGameAddEdit}
          onClose={handleGameEditClose}
        />
      )}

      {selectedMatchNode && (
        <Dialog
          open={openBracketEdit}
          onClose={() => setOpenBracketEdit(false)}
          fullWidth
          maxWidth="sm"
          fullScreen={isMobile}>
          <DialogContent>
            <Typography variant="h6">Edit {selectedMatchNode.name}</Typography>
            <NodePlayers
              matchNode={selectedMatchNode}
              bracket={bracket}
              onUpdate={handleNodePlayersUpdate}
            />
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => setOpenBracketEdit(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default SingleBracket;
